import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { ISearchEvent } from 'src/app/components/search-form/search-form.constants';
import { CAN_ALWAYS_OPERATE_ISV } from 'src/app/modules/isv/isv.constants';
import { BasicModalComponent } from 'src/app/modules/shared/components/basic-modal/basic-modal.component';
import { CLOSE_BUTTON } from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { ETableActionType, ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { INavigationEvent } from 'src/app/modules/shared/models/navigation.model';
import { IObjectKey } from 'src/app/modules/shared/models/objects';
import { IDashboardOperation, IDashboardOperationRequest } from 'src/app/modules/shared/models/operation.model';
import { ATHORA_COMPANY_IDS, EPolicyTypes, IMovement, IPolicyDetail, IPolicyOperation, IPolicyValuePayment, ISV_COMPANY_IDS } from 'src/app/modules/shared/models/policy.model';
import { cannot_cancel_operations, EDashboardStatus, INTERNAL_MOVEMENT_ID_REGEX, UUID_REGEX } from 'src/app/pages/dashboard/dashboard.constants';
import { DashboardService } from 'src/app/pages/dashboard/dashboard.service';
import { BADGE_STYLE_PREFIX } from 'src/app/pages/search/search.constants';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { PAYMENTS_TABLE_KEYS, TRANSACTIONS_TABLE_KEYS, UNI20_PAYMENTS_TABLE_KEYS } from '../../pages/policy-detail/policy-detail.constants';
import { PolicyService } from '../../policy.service';
import { EPolicyCompany, EPolicyStatus } from './../../../shared/models/policy.model';
import moment = require('moment');

@Component({
  selector: 'app-policy-header',
  templateUrl: './policy-header.component.html',
  styleUrl: './policy-header.component.scss',
})
export class PolicyHeaderComponent implements OnInit, OnDestroy {

  @Input() keys: IObjectKey[] = [];
  @Input() showButtons = false;
  @Input() isContractorDocumentExpired: boolean = false;
  @Input() registryDetailsError: boolean = false;
  @Output() navigationRequested = new EventEmitter<INavigationEvent>();
  policyTransactions: IDashboardOperation[] = [];
  destroy$ = new Subject<boolean>();

  readonly dialog = inject(MatDialog);

  constructor(
    private readonly _policyService: PolicyService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _spinner: SpinnerService,
    private readonly _dashboardService: DashboardService,
    private readonly _alertService: AlertService,
    private readonly _router: Router
  ) { }

  ngOnInit(): void {
    LoggerService.logInfo(this.constructor.name, 'ngOnInit', this.detail);
    LoggerService.logInfo(this.constructor.name, 'ngOnInit', this.keys);
    this.retrievePolicyTransactions();

    this._policyService.refreshDetail.pipe(takeUntil(this.destroy$)).subscribe(refresh => {
      if (refresh) {
        this.retrievePolicyTransactions();
      }
    })
  }

  goTo(operationData: any) {
    const opCode = CommonUtils.checkAndGet(operationData, 'operationCode', null);
    const company = CommonUtils.getCompanyCode(this.company);
    if (company) {
      const url = `/policies/${this._policyService.contractNumber}/${company}/operations/${opCode}`;
      LoggerService.logInfo(this.constructor.name, 'goTo', { opCode, url });
      this._policyService.setOperationToCancel(CommonUtils.checkAndGet(operationData, 'element', null)?.operationId || null);
      this.navigationRequested.emit({
        url: url,
        state: {
          element: CommonUtils.checkAndGet(operationData, 'element', null),
          simulateOperation: this._authenticationService.isHelpDeskAccount(this._policyService.policyType)
        }
      })
    } else {
      this._alertService.openErrorMessage('Codice compagnia non riconosciuto', '');
    }
  }

  goBack() {
    const isOperationPath = this._router.url.includes('operations');
    let url = '';
    let state: ISearchEvent = null;
    if (isOperationPath) {
      url = `/policies/${this._policyService.contractNumber}`;
      this._policyService.refreshPolicyDetail(true);
    } else {
      state = {
        type: 'BASIC',
        rawForm: !!this.ndg ? { ndg: this.ndg } : null,
        skipList: false
      }
      url = '/search';
    }
    LoggerService.logInfo(this.constructor.name, 'goBack', { url, state });
    this.navigationRequested.emit({ url, state });
  }

  getPlaceholders(keysLength: number): number[] {
    const array = CommonUtils.getPlaceholders(keysLength, 4);
    return array;
  }

  getBadgeStyle(policyType: EPolicyTypes): string {
    switch(policyType) {
      case EPolicyTypes.TCM:
      case EPolicyTypes.TCM_PPI:
        return BADGE_STYLE_PREFIX + EPolicyTypes.TCM.toLowerCase();
      case EPolicyTypes.PIP:
        return BADGE_STYLE_PREFIX + EPolicyTypes.FONDO_PENSIONE.toLowerCase().replace(' ', '-');
      case EPolicyTypes.TRADIZIONALE:
      case EPolicyTypes.PREVIDENZIALE:
      case EPolicyTypes.PREVIDENZIALI:
      case EPolicyTypes.FONDO_PENSIONE:
        return BADGE_STYLE_PREFIX + policyType.toLowerCase().replace(' ', '-');
      case EPolicyTypes.MULTIRAMO:
      case EPolicyTypes.UNIT:
      default:
        return BADGE_STYLE_PREFIX + policyType.toLowerCase();
    }
  }

  getStatusBadgeStyle(policyStatus: string): string {
    return CommonUtils.getStatusBadgeStyle(policyStatus);
  }

  getValueByPath(detail: any, item: IObjectKey): string {
    const value = CommonUtils.getValueByPath(detail, item);
    return value;
  }

  getCompanyLogo(): string {
    const logo = CommonUtils.getCompanyImage(this.company);
    return logo;
  }

  openTransactionsModal(): void {
    this.dialog.open(BasicModalComponent, {
      data: {
        title: "Operazioni effettuate",
        tableKeys: TRANSACTIONS_TABLE_KEYS,
        tableValues: this.policyTransactions,
        buttons: [
          CLOSE_BUTTON
        ]
      },
      panelClass: 'modal-box'
    })
      .afterClosed()
      .pipe(take(1))
      .subscribe(outcome => {
        if (outcome && outcome.element) {
          this.manageOperationsActions(outcome);
        }
      }
    )
  }

  manageOperationsActions(outcome: any): void {
    const actionKey: string = CommonUtils.checkAndGet(outcome, 'action', null);
    const element: IDashboardOperation = CommonUtils.checkAndGet(outcome, 'element', null);
    if (actionKey === ETableActionType.DELETE) {
      if (element?.status === EDashboardStatus.DRAFT || (element?.operationId === '_VCBMU' && this.company === EPolicyCompany.ATHORA)) {
        this.deleteDraft();
      } else {
        if (element?.operationId) {
          const newOperationCode = this.getCancellationCode(element?.operationId, this.company);
          this.goTo({
            operationCode: newOperationCode,
            element: element
          });
        }
      }
    } else if (actionKey === ETableActionType.OPERATION) {
      const contractNumber = element?.bperContractNumber;
      const operationId = element?.operationId.includes('_SWIT') && CommonUtils.getCompanyCode(this.company) === 'ath' ? '_SWIT' : element?.operationId;
      const company = CommonUtils.getCompanyCode(this.company);
      if (contractNumber && operationId && company) {
        this._policyService.setRouteIds(contractNumber, operationId);
        outcome.simulateOperation = false;
        this._router.navigate([`/policies/${contractNumber}/${company}/operations/${operationId}`], {
          state: outcome
        });
      }
    }
  }

  openPaymentsModal(): void {
    const tableKeys: ITableKey[] = this.company === EPolicyCompany.ISV ? UNI20_PAYMENTS_TABLE_KEYS : PAYMENTS_TABLE_KEYS;
    this.dialog.open(BasicModalComponent, {
      data: {
        title: "Quietanze e pagamenti",
        tableKeys: tableKeys,
        tableValues: this.paymentsList,
        buttons: [
          CLOSE_BUTTON
        ]
      },
      panelClass: 'modal-box'
    })
  }

  getCancellationCode(operationId: string, companyId: string): string {
    if (CommonUtils.arrayInclude(ATHORA_COMPANY_IDS, companyId.toLowerCase())) {
      switch (operationId) {
        case '_RISTO':
          return '_ARIST';
        case '_RISPA':
          return '_ARISP';
        default:
          return '_ANAPP';
      }
    } else if (CommonUtils.arrayInclude(ISV_COMPANY_IDS, companyId.toLowerCase())) {
      return '_ANAPP';
    } else {
      return null;
    }
  }

  deleteDraft() {
    this._spinner.show();
    this._policyService.deleteDraft()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          LoggerService.logInfo(this.constructor.name, 'deleteDraft', { resp });
          this._alertService.openSuccessMessage('Bozza eliminata con successo');
          this._policyService.refreshPolicyDetail(true);
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'deleteDraft', { error });
          this._alertService.openErrorMessage('', 'deleteDraft', error);
          this._spinner.hide();
        }
      })
  }

  retrievePolicyTransactions(): void {
    this._spinner.show();
    const body: IDashboardOperationRequest = {
      bperContractNumber: this._policyService.contractNumber,
    }
    LoggerService.logInfo(this.constructor.name, 'retrievePolicyTransactions', { body });
    this._dashboardService.getDashboardOperations(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          LoggerService.logInfo(this.constructor.name, 'retrievePolicyTransactions', { resp });
          const transactions = CommonUtils.manageDashboardData(CommonUtils.checkAndGet(resp, 'data', null));
          this.policyTransactions = this.manageTransactionsObject(transactions);
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrievePolicyTransactions', { error });
          this._alertService.openErrorMessage('Errore nel recupero delle operazioni effettuate', 'retrievePolicyTransactions', error);
          this._spinner.hide();
        }
      })
  }

  canCancelOperation(transaction: IDashboardOperation): boolean {
    if (this._authenticationService.isHelpDeskAccount(this._policyService.policyType) ||
        !this._authenticationService.hasWriteAbilitation(this._policyService.policyType)
    ) {
      return false;
    }
    if (transaction?.status === EDashboardStatus.DRAFT) {
      return true;
    } else if (cannot_cancel_operations.includes(transaction?.status as EDashboardStatus)) {
      return false;
    } else if (transaction?.movementId?.match(UUID_REGEX) || transaction?.movementId?.match(INTERNAL_MOVEMENT_ID_REGEX)) {
      return false;
    } else {
      return this.company !== EPolicyCompany.ISP && moment(transaction?.operationDate).isSame(moment(), 'day');
    }
  }

  manageTransactionsObject(newTransactions: any[]): IDashboardOperation[] {
    const transactionsList: IDashboardOperation[] = [];
    if (!CommonUtils.isVoid(newTransactions)) {
      // policyTransactions --> operazioni effettuate tramite il portale BPER Suite
      newTransactions.forEach(transaction => {
        transaction.showOperation = !cannot_cancel_operations.includes(transaction?.status as EDashboardStatus);
        transaction.cancelOperation = this.canCancelOperation(transaction);
        transactionsList.push(transaction);
      })
    }
    if (!CommonUtils.isVoid(this.oldTransactions)) {
      // oldTransactions --> operazioni provenienti da Athora/Intesa (prima del portale BPER Suite)
      this.oldTransactions.forEach((oldTransaction: IMovement) => {
        const checkMovementId = newTransactions.find(transaction => transaction.movementId === oldTransaction.movementId);
        if (!checkMovementId) {
          transactionsList.push({
            id: null,
            movementId: oldTransaction?.movementId,
            operationDescription: oldTransaction?.operationDescription,
            operationDate: oldTransaction?.emissionDate,
            effectDate: oldTransaction?.effectiveDate,
            userId: null,
            benefitAtDate: oldTransaction?.benefitAtDate,
            status: [EPolicyCompany.ISV, EPolicyCompany.ISLD, EPolicyCompany.ISP].includes(this.company as EPolicyCompany)
              ? oldTransaction?.movementStatus
                  ? (() => {
                      const formatted = oldTransaction.movementStatus.toLowerCase().replace(/^\w/, c => c.toUpperCase());
                      return formatted === "Eseguita" ? EDashboardStatus.COMPLETED : formatted;
                    })()
                  : undefined
              : EDashboardStatus.COMPLETED,
            showIcon: false
          });
        }
      })
    }
    // Trova la transazione COMPLETED più recente
    const completedTransactions = transactionsList
      .filter(tx => tx.completionDate && tx.status === EDashboardStatus.COMPLETED);
    const latestCompletedTransaction = completedTransactions
      .sort((a, b) => moment(b.completionDate).diff(moment(a.completionDate)))[0];
    const latestCompletionDate = latestCompletedTransaction
      ? moment(latestCompletedTransaction.completionDate)
      : null;
    if (this.company === EPolicyCompany.ISV) {
      // caso uni20
      this._policyService.canOperateToday =
        latestCompletionDate &&
        !CAN_ALWAYS_OPERATE_ISV.includes(latestCompletedTransaction?.operationId)
          ? latestCompletionDate?.isBefore(moment(), 'day')
          : true;
    } else if (this.company === EPolicyCompany.ISP) {
      this._policyService.canOperateToday = true;
    } else {
      // caso athora e uni30
      this._policyService.canOperateToday = latestCompletionDate
        ? latestCompletionDate?.isBefore(moment(), 'day')
        : true;
    }
    this._policyService.transactionList = transactionsList;
    this._policyService.manageOperations();
    return transactionsList;
  }

  get policyId(): string {
    return this._policyService.policyId;
  }

  get operationId(): string {
    return this._policyService.operationId;
  }

  get ndg(): string {
    return this._policyService.ndg;
  }

  get operations(): IPolicyOperation[] {
    return this._policyService.operations;
  }

  get detail(): IPolicyDetail {
    return this._policyService.policyDetail;
  }

  get company(): string {
    return this._policyService.companyId;
  }

  get policyType(): EPolicyTypes {
    return this._policyService.policyType;
  }

  get policyStatus(): string {
    return CommonUtils.checkAndGet(this.detail, 'summary.policyStatus', null);
  }

  get oldTransactions(): IMovement[] {
    return CommonUtils.checkAndGet(this._policyService.policyDetail, 'policyValue.movementsList', []);
  }

  get paymentsList(): IPolicyValuePayment[] {
    const paymentsList = CommonUtils.checkAndGet(this._policyService.policyDetail, 'policyValue.paymentsList', null);
    if (paymentsList) {
      paymentsList.forEach(payment => {
        payment.status = payment.paymentStatus;
        payment.chargeDate = payment.paymentStatus === 'Incassato' ? payment.paymentDate : 'N/A';
      })
    }
    return paymentsList;
  }

  get isOFSAuthorized(): boolean {
    return !CommonUtils.checkAndGet(this.detail, 'isUserOperatingOFSWithoutCustomerOFS', null);
  }

  get showOperationsButton(): boolean {
    // se polizza previdenziale e uni20 allora non può operare
    if (this.company === EPolicyCompany.ISV && this.policyType === EPolicyTypes.PREVIDENZIALI) {
      return false;
    }
    return (
      this._policyService.policyDetail.summary.policyStatus !== EPolicyStatus.ESTINTA &&
      !this.registryDetailsError &&
      !this._policyService.policyHasConstraint &&
      (this.filteredOperations.length > 0 &&
      (this._authenticationService.isHelpDeskAccount(this.policyType) ||
        this._authenticationService.hasWriteAbilitation(this.policyType)) &&
      this.isOFSAuthorized)
    );
  }

  get operationsButtonName(): string {
    return this.isHelpDeskAccount ? 'simulazione operazioni' : 'inserimento operazioni'
  }

  get filteredOperations(): IPolicyOperation[] {
    if (this.isContractorDocumentExpired) {
      return this.operations.filter(op => op.operationCode === '_SIMOR');
    }
    return this.operations;
  }

  get isHelpDeskAccount(): boolean {
    return this._authenticationService.isHelpDeskAccount(this.policyType);
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
